import { useState, useEffect } from 'react'
import { useNavigate, useParams, Link, useSearchParams, useLocation } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import type { Unit, UnitWorkshop } from '@/types'
import {
  ArrowLeft,
  Save,
  Video,
  Calendar,
  Users,
  Link as LinkIcon,
  Edit
} from 'lucide-react'

interface Teacher {
  id: string
  profile: {
    first_name: string
    last_name: string
  }
}

interface CohortOption {
  id: string
  name: string
}

interface WorkshopFormData {
  unit_id: string
  cohort_id: string
  teacher_id: string
  title: string
  description: string
  scheduled_date: string
  duration_minutes: number
  meeting_url: string
  max_participants: number | null
  is_mandatory: boolean
  is_active: boolean
}

export default function WorkshopForm() {
  const { workshopId } = useParams()
  const [searchParams] = useSearchParams()
  const unitIdFromUrl = searchParams.get('unit')
  const navigate = useNavigate()
  const location = useLocation()
  const isEditing = Boolean(workshopId) && workshopId !== 'new'
  const isViewMode = isEditing && !location.pathname.endsWith('/edit')

  const [units, setUnits] = useState<Unit[]>([])
  const [teachers, setTeachers] = useState<Teacher[]>([])
  const [cohorts, setCohorts] = useState<CohortOption[]>([])
  const [formData, setFormData] = useState<WorkshopFormData>({
    unit_id: unitIdFromUrl || '',
    cohort_id: '',
    teacher_id: '',
    title: '',
    description: '',
    scheduled_date: '',
    duration_minutes: 60,
    meeting_url: '',
    max_participants: null,
    is_mandatory: true,
    is_active: true
  })
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    loadData()
    if (isEditing) {
      loadWorkshop()
    }
  }, [workshopId])

  const loadData = async () => {
    try {
      const [unitsRes, teachersRes, cohortsRes] = await Promise.all([
        supabase.from('units').select('*').order('order_num', { ascending: true }),
        supabase.from('teachers').select('id, profile:profiles!profile_id(first_name, last_name)'),
        supabase.from('cohorts').select('id, name').eq('is_active', true).order('name', { ascending: true })
      ])

      if (unitsRes.error) throw unitsRes.error
      if (teachersRes.error) throw teachersRes.error

      setUnits((unitsRes.data as unknown as Unit[]) || [])
      setTeachers((teachersRes.data as unknown as Teacher[]) || [])
      setCohorts((cohortsRes.data as CohortOption[]) || [])
    } catch (error) {
      console.error('Error loading data:', error)
    }
  }

  const loadWorkshop = async () => {
    setIsLoading(true)
    try {
      const { data, error } = await supabase
        .from('unit_workshops')
        .select('*')
        .eq('id', workshopId as string)
        .single()

      if (error) throw error

      const workshopData = data as unknown as UnitWorkshop

      setFormData({
        unit_id: workshopData.unit_id,
        cohort_id: workshopData.cohort_id || '',
        teacher_id: workshopData.teacher_id || '',
        title: workshopData.title,
        description: workshopData.description || '',
        scheduled_date: workshopData.scheduled_date.slice(0, 16),
        duration_minutes: workshopData.duration_minutes,
        meeting_url: workshopData.meeting_url || '',
        max_participants: workshopData.max_participants,
        is_mandatory: workshopData.is_mandatory ?? false,
        is_active: workshopData.is_active
      })
    } catch (error) {
      console.error('Error loading workshop:', error)
      setError('Error al cargar el taller')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsSaving(true)

    try {
      const rawUrl = formData.meeting_url?.trim() || ''
      const normalizedUrl = rawUrl && !/^https?:\/\//i.test(rawUrl)
        ? `https://${rawUrl.replace(/^\/+/, '')}`
        : rawUrl

      const workshopData = {
        unit_id: formData.unit_id,
        cohort_id: formData.cohort_id || null,
        teacher_id: formData.teacher_id || null,
        title: formData.title,
        description: formData.description || null,
        scheduled_date: new Date(formData.scheduled_date).toISOString(),
        duration_minutes: formData.duration_minutes,
        meeting_url: normalizedUrl || null,
        max_participants: formData.max_participants,
        is_mandatory: formData.is_mandatory,
        is_active: formData.is_active
      }

      if (isEditing) {
        const { error } = await (supabase
          .from('unit_workshops') as any)
          .update({
            ...workshopData,
            updated_at: new Date().toISOString()
          })
          .eq('id', workshopId as string)

        if (error) throw error
      } else {
        const { error } = await (supabase
          .from('unit_workshops') as any)
          .insert(workshopData)

        if (error) throw error
      }

      navigate('/admin/workshops')
    } catch (error: any) {
      console.error('Error saving workshop:', error)
      setError(error.message || 'Error al guardar el taller')
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0055A4]" />
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/admin/workshops"
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isViewMode ? 'Visualizar Taller' : (isEditing ? 'Editar Taller' : 'Nuevo Taller')}
            </h1>
            <p className="text-gray-600">
              {isViewMode ? 'Vista previa del taller' : (isEditing ? 'Modifica los datos del taller' : 'Crea un nuevo taller en vivo')}
            </p>
          </div>
        </div>
        {isViewMode && (
          <Link
            to={`/admin/workshops/${workshopId}/edit`}
            className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Edit className="w-5 h-5" />
            Editar
          </Link>
        )}
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
            {error}
          </div>
        )}

        {/* Basic Info */}
        <div className="bg-white rounded-xl p-6 shadow-sm space-y-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-indigo-100">
              <Video className="w-5 h-5 text-indigo-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Información del Taller</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label htmlFor="workshop-cohort" className="block text-sm font-medium text-gray-700 mb-1">
                Cohorte *
              </label>
              <select
                id="workshop-cohort"
                value={formData.cohort_id}
                onChange={(e) => setFormData(prev => ({ ...prev, cohort_id: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
                required
                disabled={isViewMode}
              >
                <option value="">Seleccionar cohorte...</option>
                {cohorts.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="workshop-unit" className="block text-sm font-medium text-gray-700 mb-1">
                Unidad
              </label>
              <select
                id="workshop-unit"
                value={formData.unit_id}
                onChange={(e) => setFormData(prev => ({ ...prev, unit_id: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
                required
                disabled={isViewMode}
              >
                <option value="">Seleccionar unidad...</option>
                {units.map(unit => (
                  <option key={unit.id} value={unit.id}>
                    Unidad {unit.order_num}: {unit.title}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="workshop-teacher" className="block text-sm font-medium text-gray-700 mb-1">
                Profesor
              </label>
              <select
                id="workshop-teacher"
                value={formData.teacher_id}
                onChange={(e) => setFormData(prev => ({ ...prev, teacher_id: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
                disabled={isViewMode}
              >
                <option value="">Seleccionar profesor...</option>
                {teachers.map(teacher => (
                  <option key={teacher.id} value={teacher.id}>
                    {teacher.profile?.first_name} {teacher.profile?.last_name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="workshop-title" className="block text-sm font-medium text-gray-700 mb-1">
              Título del Taller
            </label>
            <input id="workshop-title" type="text" value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Ej: Taller de Conversación - Saludos"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
              required
              disabled={isViewMode}
            />
          </div>

          <div>
            <label htmlFor="workshop-description" className="block text-sm font-medium text-gray-700 mb-1">
              Descripción (opcional)
            </label>
            <textarea
              id="workshop-description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Describe el contenido del taller..."
              rows={3}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
              disabled={isViewMode}
            />
          </div>
        </div>

        {/* Schedule */}
        <div className="bg-white rounded-xl p-6 shadow-sm space-y-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-[#0055A4]/10">
              <Calendar className="w-5 h-5 text-[#0055A4]" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Programacion</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="workshop-date" className="block text-sm font-medium text-gray-700 mb-1">
                Fecha y Hora
              </label>
              <input
                type="datetime-local"
                id="workshop-date"
                value={formData.scheduled_date}
                onChange={(e) => setFormData(prev => ({ ...prev, scheduled_date: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
                required
                disabled={isViewMode}
              />
            </div>
            <div>
              <label htmlFor="workshop-duration" className="block text-sm font-medium text-gray-700 mb-1">
                Duracion (minutos)
              </label>
              <input
                type="number"
                min="15"
                max="180"
                id="workshop-duration"
                value={formData.duration_minutes}
                onChange={(e) => setFormData(prev => ({ ...prev, duration_minutes: Number.parseInt(e.target.value) || 60 }))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
                disabled={isViewMode}
              />
            </div>
          </div>
        </div>

        {/* Meeting Details */}
        <div className="bg-white rounded-xl p-6 shadow-sm space-y-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-green-100">
              <LinkIcon className="w-5 h-5 text-green-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Detalles de la Reunion</h2>
          </div>

          <div>
            <label htmlFor="workshop-url" className="block text-sm font-medium text-gray-700 mb-1">
              URL de la Reunion (Zoom, Meet, etc.)
            </label>
            <input
              type="url"
              id="workshop-url"
              value={formData.meeting_url}
              onChange={(e) => setFormData(prev => ({ ...prev, meeting_url: e.target.value }))}
              placeholder="https://zoom.us/j/..."
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
              disabled={isViewMode}
            />
          </div>

          <div>
            <label htmlFor="workshop-max" className="block text-sm font-medium text-gray-700 mb-1">
              Máximo de Participantes (opcional)
            </label>
            <input
              type="number"
              min="1"
              id="workshop-max"
              value={formData.max_participants || ''}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                max_participants: e.target.value ? Number.parseInt(e.target.value) : null
              }))}
              placeholder="Sin limite"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
              disabled={isViewMode}
            />
            <p className="text-xs text-gray-500 mt-1">
              Dejar vacio para sin limite de participantes
            </p>
          </div>
        </div>

        {/* Options */}
        <div className="bg-white rounded-xl p-6 shadow-sm space-y-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-yellow-100">
              <Users className="w-5 h-5 text-yellow-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Opciones</h2>
          </div>

          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="is_mandatory"
                checked={formData.is_mandatory}
                onChange={(e) => setFormData(prev => ({ ...prev, is_mandatory: e.target.checked }))}
                className="w-4 h-4 text-[#0055A4] border-gray-300 rounded focus:ring-[#0055A4] disabled:cursor-not-allowed"
                disabled={isViewMode}
              />
              <label htmlFor="is_mandatory" className="text-sm text-gray-700">
                Taller obligatorio (requerido para desbloquear el examen)
              </label>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                className="w-4 h-4 text-[#0055A4] border-gray-300 rounded focus:ring-[#0055A4] disabled:cursor-not-allowed"
                disabled={isViewMode}
              />
              <label htmlFor="is_active" className="text-sm text-gray-700">
                Taller activo (visible para estudiantes)
              </label>
            </div>
          </div>
        </div>

        {/* Actions */}
        {!isViewMode && (
          <div className="flex justify-end gap-3">
            <Link
              to="/admin/workshops"
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancelar
            </Link>
            <button
              type="submit"
              disabled={isSaving || !formData.unit_id}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#0055A4] text-white rounded-lg hover:bg-[#004080] transition-colors disabled:opacity-50"
            >
              <Save className="w-5 h-5" />
              {isSaving ? 'Guardando...' : (isEditing ? 'Guardar Cambios' : 'Crear Taller')}
            </button>
          </div>
        )}
      </form>
    </div>
  )
}
